import React from 'react';
import { IconMic, IconSend } from '@/assets/images';
import AudioRecorder from '@/features/chats/sub-features/audio-recorder/ui/AudioRecorder';
import AttachmentMenu from '@/features/chats/ui/Conversation/components/AttachmentMenu';
import EmojiButton from '@/components/EmojiButton';
import PropTypes from 'prop-types';
import { useLanguage } from '@/localization/useLanguage';
import { useChatInput } from '../../viewmodels';
import { chatInputRef } from './chatInputRef';
import MentionUser from '../components/groupParticipants/MentionUser';
import './ChatInput.scss';
import { AppIcon, ContentEditableInput } from '@/components';

/**
 * Pure rendering component for Chat Input.
 * All logic (draft, typing, mentions, emoji, audio, send) extracted to useChatInput ViewModel.
 */
const ChatInput = ({
   onSend,
   activeChatJid,
   disableDraft = true,
   fileId = null,
   placeholder,
   children,
   hasReply,
   isAttachment,
}) => {
   const { t } = useLanguage();
   const inputPlaceholder = placeholder || t('chat.INPUT_PLACEHOLDER');
   const {
      isAudioEnabled,
      isAttachmentEnabled,
      isRecording,
      setIsRecording,
      inputRef,
      hasText,
      isMultiLine,
      showMention,
      isMentionEnabled,
      selectedIndex,
      activeChatId,
      filteredParticipants,
      sendMessage,
      handleInput,
      handleKeyDown,
      saveSelection,
      handleEmoji,
      handleUserSelect,
      handleAudioComplete,
      handlePaste,
   } = useChatInput({ activeChatJid, fileId, disableDraft, onSend, isAttachment });

   if (isRecording) {
      return isAudioEnabled ? <AudioRecorder activeChatJid={activeChatJid} onComplete={handleAudioComplete} /> : null;
   }

   const renderActionBtn = () => {
      if (hasText || !!fileId) {
         return (
            <button
               className="chat-send-btn postpreview-chat-send-btn ct-flexCenter ct-cursor"
               aria-label={t('common.SEND')}
               onClick={() => sendMessage()}
            >
               <AppIcon name="send" icon={<IconSend />} />
            </button>
         );
      }
      if (isAudioEnabled && isAttachmentEnabled) {
         return (
            <button
               className="chat-mic-btn ct-flexCenter ct-cursor"
               aria-label={t('common.MICROPHONE')}
               onClick={() => setIsRecording(true)}
            >
               <IconMic className="ct-icon" />
            </button>
         );
      }
      return null;
   };

   return (
      <div className={`chat-input-container ct-flex ct-gap16 ${isMultiLine || hasReply ? 'ct-aiEnd' : 'ct-aiCenter'}`}>
         <div className={`chat-input-main-wrapper ct-flex ct-fcol ct-flex1 ${hasReply ? 'has-reply-width' : ''}`}>
            {children}
            <div
               className={`chat-input-wrapper ct-flex ct-gap12 ${isMultiLine ? 'ct-aiEnd multiline' : 'ct-aiCenter sigleline'} ${hasReply ? 'has-reply' : ''} ${!isAttachmentEnabled ? 'no-attachment' : ''}`}
            >
               <EmojiButton insertEmoji={handleEmoji} />

               <div className="chat-input-box ct-flex ct-aiCenter ct-flex1">
                  <ContentEditableInput
                     id="typingContainer"
                     contentEditable
                     suppressContentEditableWarning
                     aria-multiline={true}
                     aria-label={inputPlaceholder}
                     className="chat-input-field ct-scrollable"
                     ref={node => {
                        inputRef.current = node;
                        chatInputRef.current = node;
                     }}
                     onInput={handleInput}
                     onKeyDown={handleKeyDown}
                     onKeyUp={saveSelection}
                     onMouseUp={saveSelection}
                     onBlur={saveSelection}
                     data-placeholder={inputPlaceholder}
                     data-has-text={hasText}
                     onPaste={handlePaste}
                  />

                  {isMentionEnabled && showMention && activeChatId && (
                     <MentionUser
                        participants={filteredParticipants}
                        selectedIndex={selectedIndex}
                        onUserSelect={handleUserSelect}
                     />
                  )}
               </div>

               {isAttachmentEnabled && <AttachmentMenu activeChatId={activeChatId} />}
            </div>
         </div>

         {renderActionBtn()}
      </div>
   );
};

ChatInput.propTypes = {
   onSend: PropTypes.func.isRequired,
   activeChatJid: PropTypes.string,
   fileId: PropTypes.string,
   disableDraft: PropTypes.bool,
   placeholder: PropTypes.string,
   hasReply: PropTypes.bool,
   isAttachment: PropTypes.bool,
   children: PropTypes.node,
};

export default ChatInput;
